import { useEffect, useRef } from 'react';
import { sendApiRequest } from 'lib/api/public';
import type { FormTourConfig } from './formTour';
import type { OnboardingTask } from './onboarding';

export const CONNECT_CALENDAR_TASK_ID = 'connect_calendar';

const CALENDARS_ROW_TEST_ID = '@calendars-row';

/** Auto-complete Connect Calendar when at least one calendar is already connected. */
export function useConnectCalendarAutoComplete(
  tasks: OnboardingTask[],
  markTasksCompleted: (taskIds: string[]) => Promise<boolean>,
) {
  const attemptedRef = useRef(false);

  useEffect(() => {
    const task = tasks.find(t => t.id === CONNECT_CALENDAR_TASK_ID);

    if (!task || task.completed || attemptedRef.current) {
      if (task?.completed) {
        attemptedRef.current = false;
      }
      return;
    }

    attemptedRef.current = true;

    void sendApiRequest({ path: '/integrations/calendars/list', method: 'GET' })
      .then(async response => {
        const list = Array.isArray(response)
          ? response
          : ((response as { list?: unknown[] } | null)?.list ?? []);

        if (list.length === 0) {
          attemptedRef.current = false;
          return;
        }

        const success = await markTasksCompleted([CONNECT_CALENDAR_TASK_ID]);
        if (!success) {
          attemptedRef.current = false;
        }
      })
      .catch(() => {
        attemptedRef.current = false;
      });
  }, [tasks, markTasksCompleted]);
}

export const connectCalendarTour = {
  taskId: CONNECT_CALENDAR_TASK_ID,
  storageKey: 'onboarding-connect-calendar-tour-step',
  startPath: '/settings/integrations',
  activePathPrefix: '/settings',
  navStepCount: 1,
  overlineLabel: 'Connect Calendar Tour',
  steps: [
    {
      title: 'Open Settings',
      description: 'Click Settings in the sidebar, then open Integrations to connect your calendar.',
      targetTestIds: ['@sidebar-settings'],
    },
    {
      title: 'Open Integrations',
      description: 'In the settings sidebar, choose Integrations.',
      targetTestIds: ['@settings-integrations'],
      disableBack: true,
    },
    {
      title: 'Add Calendar',
      description: 'Click Add Calendar to connect Google or Microsoft Calendar.',
      targetTestIds: ['@calendars-add'],
      closeTargetTestIdsOnBack: ['@calendar-add-modal'],
    },
    {
      title: 'Choose a calendar provider',
      description:
        'Connect Google or Microsoft Calendar. The guide finishes when your calendar appears in the list.',
      targetTestIds: ['@calendar-provider-google', '@calendar-provider-microsoft'],
      highlightTargetTestIds: ['@calendar-provider-options'],
      highlightExpandModal: true,
      contextTargetTestIds: ['@calendar-add-modal'],
      closeTargetTestIdsOnBack: ['@calendar-add-modal'],
    },
  ],
  stepActions: {
    0: {
      type: 'wait-for-target',
      advanceTo: 1,
      waitForTargetTestIds: ['@settings-integrations'],
    },
    1: {
      type: 'wait-for-target',
      advanceTo: 2,
      waitForTargetTestIds: ['@calendars-add'],
    },
    2: {
      type: 'wait-for-target',
      advanceTo: 3,
      waitForTargetTestIds: ['@calendar-add-modal'],
    },
    3: {
      type: 'complete-when-target',
      waitForTargetTestIds: [CALENDARS_ROW_TEST_ID],
    },
  },
} as const satisfies FormTourConfig;
